import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { waitForMs } from "../../../../apiCacheCalls";
import { useCustomerAnalytics } from "../../../../services/hooks/useCustomerAnalytics";
import { useModalHook } from "../../../../services/hooks/useModal";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../../services/hooks/useTranslationI18";
import { setReceiptNote } from "../../../../services/redux/slices/cartAndLoyaltySlice";
import {
  setBackPage,
  setCurrentDeliveryConfig,
} from "../../../../services/redux/slices/deliverySlice";
import {
  LoadingActionEnum,
  decreasePendingOperations,
  increasePendingOperations,
  pushToLoadingStack,
  setAddressSelectionDrawerState,
  setLoading,
} from "../../../../services/redux/slices/globalComponentsToggleSlice";
import { TransformOfferType } from "../../../../services/redux/types/TypeHelper";
import CartItem from "../../../../utils/CartItem";
import {
  CUSTOMER_ANALYTICS_EVENT,
  OrderTypesEnum,
  PagesEnum,
  URL_PARAMS,
} from "../../../../utils/enums";
import {
  getPageLink,
  removeUrlParam,
  setUrlParam,
} from "../../../../utils/help/navigation/navigation";
import {
  getDisabledItemsPerOrderType,
  getOrderTypeKeyStringByValue,
  isOnlyOneOrderTypePerFranchise,
  isOrderTypeSupported,
} from "../../../../utils/help/orderType/orderType";
import { Franchise, FranchiseDeliveryConfig } from "../../../../utils/types";
import { ArrowIcon } from "../../../screens/S-Cart/Cart.icons";
import styles from "./OrderType.module.scss";

export enum OrderTypeToggleType {
  DEFAULT = "default",
  SELECT_ORDER_INFO = "select_order_info",
}

type OrderTypeToggleProps = {
  deliveryDropDown?: boolean;
  setOrderTypeOpened?: (opened: boolean) => void;
  orderTypeOpened?: boolean;
  setSelectedOrderType?: (
    orderType: OrderTypesEnum,
    deliveryV2Config?: FranchiseDeliveryConfig
  ) => void;
  selectedFranchise?: Franchise;
  selectedOrderType?: OrderTypesEnum;
  type?: OrderTypeToggleType;
};

export const OrderTypeToggle = (props: OrderTypeToggleProps) => {
  const deliveryDropDown = props.deliveryDropDown;
  const { t } = useTranslationI18();
  const { handleModal } = useModalHook();

  const [active, setActive] = useState(false);
  const [repeat, setRepeat] = useState(false);
  const [
    waitForCurrentDeliveryConfigUpdate,
    setWaitForCurrentDeliveryConfigUpdate,
  ] = useState(false);
  const router = useRouter();
  const dispatch = useAppDispatch();
  const addCustomerAnalytics = useCustomerAnalytics();

  const currentFranchiseConfig = useAppSelector(
    (state) => state.config.selectedFranchise?.franchiseConfig
  );

  const selectedFranchiseConfig = props.selectedFranchise?.franchiseConfig;

  const franchiseConfig =
    (selectedFranchiseConfig || currentFranchiseConfig) ?? {};

  const delivery = useAppSelector((state) => state.delivery);

  const orderTypeState = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );

  const selectedFranchise = useAppSelector(
    (state) => state.config.selectedFranchise
  );

  const selectedOrderType = props.selectedOrderType;

  const orderTypeParam = selectedOrderType || orderTypeState;

  const orderTypeDisplayed =
    props.selectedFranchise?.id === selectedFranchise?.id
      ? orderTypeParam
      : selectedOrderType;

  const cartLoyaltyV2 = useAppSelector((state) => state.cartLoyaltyV2Reducer);
  const reduxCartAndLoyalty = useAppSelector(
    (state) => state.cartAndLoyaltyReducer
  );
  const cart = cartLoyaltyV2.cart;
  const deliveryV2Config = useAppSelector(
    (state) => state.delivery.currentDeliveryConfig
  );

  const redeemedOffer = reduxCartAndLoyalty.offersArray.find((o) =>
    o.isEqual(cartLoyaltyV2.cart.redeemedOffer)
  );
  const restaurantConfig = useAppSelector(
    (state) => state.config.restaurantInfo
  )?.config;
  const itemStatusRedux = useAppSelector((state) => state.itemStatus);
  // const isHomePage = router.pathname.includes(`/${PagesEnum.Home}`);
  // const isItemPage = router.pathname.includes(`/${PagesEnum.Item}`);
  // const isAllRewardsPage = router.pathname.includes(`/${PagesEnum.AllRewards}`);
  // const isOfflineModePage = router.pathname.includes(
  //   `/${PagesEnum.OfflineMode}`
  // );
  const isCartPage = router.pathname.includes(`/${PagesEnum.Cart}`);

  const takeOutTranslation = t(
    "table-page:table-take-out",
    {},
    router.locale,
    isCartPage
      ? franchiseConfig?.takeOut?.cartScreenCustomOrderTypeName
      : franchiseConfig?.takeOut?.customOrderTypeName,
    isCartPage
      ? franchiseConfig?.takeOut?.localizedCartScreenCustomOrderTypeName
      : franchiseConfig?.takeOut?.localizedCustomOrderTypeName
  );

  const dineInTranslation = t(
    "table-page:table-dine-in",
    {},
    router.locale,
    isCartPage
      ? franchiseConfig?.dineIn?.cartScreenCustomOrderTypeName
      : franchiseConfig?.dineIn?.customOrderTypeName,
    isCartPage
      ? franchiseConfig?.dineIn?.localizedCartScreenCustomOrderTypeName
      : franchiseConfig?.dineIn?.localizedCustomOrderTypeName
  );

  const deliveryTranslation = t("table-page:table-delivery");

  const hasOnlyOneOrderType = isOnlyOneOrderTypePerFranchise(franchiseConfig);

  useEffect(() => {
    if (waitForCurrentDeliveryConfigUpdate) {
      // wait for redux-rersist to update LS
      waitForMs(10).then(() => {
        const LSCurrentFranchieDeliveryConfigId = JSON.parse(
          localStorage.getItem("persist:delivery")! ?? "{}",
          function (key, value) {
            if (key === "currentDeliveryConfig") {
              return JSON.parse(value);
            }

            return value;
          }
        )?.currentDeliveryConfig?.id;
        if (
          delivery.currentDeliveryConfig?.id ===
          LSCurrentFranchieDeliveryConfigId
        ) {
          setWaitForCurrentDeliveryConfigUpdate(false);
          window.location.reload();
        } else {
          setRepeat(!repeat);
        }
      });
    }
  }, [delivery.currentDeliveryConfig, repeat]);

  useEffect(() => {
    setGeneralOrderType(orderTypeParam, true);
  }, []);

  useEffect(() => {
    if (props.orderTypeOpened !== undefined && !hasOnlyOneOrderType) {
      setActive(props.orderTypeOpened);
    }
  }, [props.orderTypeOpened]);

  const setGeneralOrderType = (
    type?: OrderTypesEnum,
    initial: boolean = false,
    deliveryV2ConfigId?: number,
    usesMap: boolean = false
  ) => {
    if (initial) {
      return;
    }

    setUrlParam(URL_PARAMS.ORDER_TYPE, type as string);
    setActive(false);
    props.setOrderTypeOpened?.(false);

    if (initial) {
      return;
    }

    if (deliveryDropDown && !usesMap) {
      if (restaurantConfig?.loadingStateSolution) {
        dispatch(
          pushToLoadingStack({
            action: LoadingActionEnum.INCREASE,
            functionName: "setGeneralOrderType if deliveryDropDown",
            date: new Date(),
          })
        );
        dispatch(increasePendingOperations());
      } else {
        dispatch(setLoading(true));
      }
      dispatch(setAddressSelectionDrawerState(false));
      router.push(getPageLink(router, PagesEnum.Home).href);

      // Clear loading state after navigation
      if (restaurantConfig?.loadingStateSolution) {
        dispatch(
          pushToLoadingStack({
            action: LoadingActionEnum.DECREASE,
            functionName: "setGeneralOrderType if deliveryDropDown",
            date: new Date(),
          })
        );
        dispatch(decreasePendingOperations());
      } else {
        dispatch(setLoading(false));
      }

      return;
    } else if (deliveryDropDown) {
      return;
    }
    if (restaurantConfig?.loadingStateSolution) {
      dispatch(
        pushToLoadingStack({
          action: LoadingActionEnum.INCREASE,
          functionName: "setGeneralOrderType",
          date: new Date(),
        })
      );
      dispatch(increasePendingOperations());
    } else {
      dispatch(setLoading(true));
    }
    if (type === OrderTypesEnum.DELIVERYV2) {
      usesMap
        ? setUrlParam(URL_PARAMS.SHOW_DELIVERY_TOGGLE, "true")
        : removeUrlParam(URL_PARAMS.SHOW_DELIVERY_TOGGLE);
      if (
        !deliveryV2ConfigId ||
        deliveryV2ConfigId === delivery.currentDeliveryConfig?.id
      ) {
        // Ensure loading state is cleared before page reload
        if (restaurantConfig?.loadingStateSolution) {
          dispatch(
            pushToLoadingStack({
              action: LoadingActionEnum.DECREASE,
              functionName: "setGeneralOrderType",
              date: new Date(),
            })
          );
          dispatch(decreasePendingOperations());
        } else {
          dispatch(setLoading(false));
        }
        window.location.reload();
      } else {
        setWaitForCurrentDeliveryConfigUpdate(true);
      }
    } else {
      removeUrlParam(URL_PARAMS.SHOW_DELIVERY_TOGGLE);
      // Ensure loading state is cleared before page reload
      if (restaurantConfig?.loadingStateSolution) {
        dispatch(
          pushToLoadingStack({
            action: LoadingActionEnum.DECREASE,
            functionName: "setGeneralOrderType",
            date: new Date(),
          })
        );
        dispatch(decreasePendingOperations());
      } else {
        dispatch(setLoading(false));
      }
      window.location.reload();
    }
  };

  const getCurrentOrderType = () => {
    const ordertype = props.selectedFranchise
      ? orderTypeDisplayed
      : orderTypeParam;
    switch (ordertype) {
      case OrderTypesEnum.TAKE_OUT:
        return takeOutTranslation;
      case OrderTypesEnum.DINE_IN:
        return dineInTranslation;
      case OrderTypesEnum.DELIVERY:
        if (franchiseConfig.delivery?.buttonTitleTableScreen) {
          return t(
            `table-page:${franchiseConfig.delivery?.buttonTitleTableScreen}`
          );
        }

        return deliveryTranslation;

      case OrderTypesEnum.DELIVERYV2:
        if (delivery.currentDeliveryConfig?.deliveryType?.displayName) {
          return t(
            `table-page:${delivery.currentDeliveryConfig?.deliveryType?.displayName}`
          );
        }

        return null;
      default:
        break;
    }
  };

  const handleToggle = (
    type: OrderTypesEnum,
    initial: boolean = false,
    deliveryV2Config?: FranchiseDeliveryConfig
  ) => {
    if (
      props.type === OrderTypeToggleType.SELECT_ORDER_INFO
      // ||
      // isHomePage ||
      // isAllRewardsPage ||
      // isItemPage ||
      // isOfflineModePage
    ) {
      if (!deliveryV2Config) {
        dispatch(setCurrentDeliveryConfig(null));
        props.setSelectedOrderType?.(type, undefined);
      }

      props.setOrderTypeOpened?.(false);
      setActive(false);

      return;
    }

    const isCarPickupSelection =
      type === OrderTypesEnum.TAKE_OUT &&
      /car\s*[- ]?\s*pickup/i.test(takeOutTranslation);
    addCustomerAnalytics(
      type === OrderTypesEnum.DINE_IN
        ? CUSTOMER_ANALYTICS_EVENT.DINE_IN_SELECTION
        : type === OrderTypesEnum.DELIVERYV2
        ? CUSTOMER_ANALYTICS_EVENT.DELIVERY_SELECTION
        : isCarPickupSelection
        ? CUSTOMER_ANALYTICS_EVENT.CAR_PICK_UP_SELECTION
        : CUSTOMER_ANALYTICS_EVENT.PICKUP_SELECTION
    );

    dispatch(setReceiptNote(undefined));
    const disabledCartItems = checkCartForDisabledItemsForNewOrderType(type);
    const orderTypeKey = getOrderTypeKeyStringByValue(type);
    const redeemeedOfferWillNotChange =
      !redeemedOffer?.orderTypes ||
      redeemedOffer?.orderTypes.includes(orderTypeKey);
    if (disabledCartItems.length === 0 && redeemeedOfferWillNotChange) {
      return setGeneralOrderType(
        type,
        initial,
        deliveryV2Config?.id,
        deliveryV2Config?.usesMap
      );
    }
    const modalText =
      disabledCartItems.length > 0 && !redeemeedOfferWillNotChange
        ? "cart:disabled-items-modal"
        : disabledCartItems.length > 0
        ? "cart:disabled-items-modal"
        : "cart:updated-offers-modal";

    setActive(false);
    handleModal &&
      handleModal(true, "DisabledItemsInCart/DisabledItemsInCart", {
        disableEscapeKeyDown: true,
        preventBackdropClick: true,
        componentProps: {
          message: modalText,
          onClick: () => {
            setGeneralOrderType(
              type,
              initial,
              deliveryV2Config?.id,
              deliveryV2Config?.usesMap
            );
            handleModal(false, "", {});
          },
        },
      });
  };

  const checkCartForDisabledItemsForNewOrderType = (type: OrderTypesEnum) => {
    const disabledItemsIds = getDisabledItemsPerOrderType(
      itemStatusRedux,
      type
    );

    const disabledCartItems = [] as {
      item: TransformOfferType<CartItem>;
      index: number;
    }[];

    cart.items.forEach((item, index) => {
      if (disabledItemsIds.includes(item.itemDetails.id)) {
        disabledCartItems.push({ item, index });
      }
    });

    return disabledCartItems;
  };

  const onClickDropDown = () => {
    if (hasOnlyOneOrderType && !deliveryDropDown) {
      return;
    }
    setActive(!active);
    props.setOrderTypeOpened?.(!active);
  };

  return (
    <div className={styles.wrapper}>
      <div
        className={`${styles.select} ${active && styles.active} ${
          props.type === OrderTypeToggleType.SELECT_ORDER_INFO &&
          //  || isHomePage ||
          //   isAllRewardsPage ||
          //   isItemPage ||
          //   isOfflineModePage
          styles.secondary_shape
        }`}
        onClick={onClickDropDown}
        data-testid={"order-type-toggle"}
      >
        <span
          className={`${!isCartPage && `bg__zero`} ${styles.select_text} ${
            styles.select_text_md
          } ${isCartPage && styles.max_width}`}
        >
          {getCurrentOrderType() || t("common:choose-order-type")}
        </span>
        {!hasOnlyOneOrderType && (
          <span className={styles.select_arrow_icon}>
            <ArrowIcon />
          </span>
        )}
      </div>

      <ul
        className={`${styles.dropdown} ${
          props.deliveryDropDown && styles.drop_up
        } ${active && styles.active_dropdown} ${
          deliveryDropDown && styles.move
        }`}
        data-testid={"all-order-types-dropdown"}
      >
        {isOrderTypeSupported(franchiseConfig, OrderTypesEnum.TAKE_OUT) &&
          !deliveryDropDown && (
            <li
              className={`${
                orderTypeParam === OrderTypesEnum.TAKE_OUT && styles.active_link
              }`}
              onClick={() => {
                handleToggle(OrderTypesEnum.TAKE_OUT);
              }}
              data-testid={"takeout-toggle"}
            >
              <a>{takeOutTranslation}</a>
            </li>
          )}

        {isOrderTypeSupported(franchiseConfig, OrderTypesEnum.DINE_IN) &&
          !deliveryDropDown && (
            <li
              className={`${
                orderTypeParam === OrderTypesEnum.DINE_IN && styles.active_link
              }`}
              onClick={() => {
                handleToggle(OrderTypesEnum.DINE_IN);
              }}
              data-testid={"dinein-toggle"}
            >
              <a>{dineInTranslation}</a>
            </li>
          )}

        {franchiseConfig.deliveryV2 &&
          franchiseConfig.deliveryV2
            .filter(
              (curfranchiseDeliveryConfig: FranchiseDeliveryConfig) =>
                !deliveryDropDown || curfranchiseDeliveryConfig.usesMap
            )
            .map(
              (
                curfranchiseDeliveryConfig: FranchiseDeliveryConfig,
                index: number
              ) =>
                !deliveryDropDown &&
                curfranchiseDeliveryConfig.showOutside && (
                  <li
                    key={index}
                    className={`${
                      orderTypeParam === OrderTypesEnum.DELIVERYV2 &&
                      delivery.currentDeliveryConfig?.id ===
                        curfranchiseDeliveryConfig.id &&
                      styles.active_link
                    }`}
                    onClick={() => {
                      props.setSelectedOrderType?.(
                        OrderTypesEnum.DELIVERYV2,
                        deliveryV2Config ?? undefined
                      );
                      dispatch(
                        setCurrentDeliveryConfig(curfranchiseDeliveryConfig)
                      );
                      dispatch(setBackPage(PagesEnum.Cart));
                      handleToggle(
                        OrderTypesEnum.DELIVERYV2,
                        undefined,
                        curfranchiseDeliveryConfig
                      );
                    }}
                    data-testid={t(
                      `table-page:${curfranchiseDeliveryConfig?.deliveryType?.displayName}`
                    )}
                  >
                    <a>
                      {t(
                        `table-page:${curfranchiseDeliveryConfig?.deliveryType?.displayName}`
                      )}
                    </a>
                    {deliveryDropDown &&
                      !curfranchiseDeliveryConfig.usesMap && <ArrowIcon />}
                  </li>
                )
            )}
      </ul>
    </div>
  );
};
